<script setup lang="ts">
/**
 * HomeScreenHeader — mirrors core/ui/layout/HomeScreenHeader.kt.
 *
 * Just typography on the page background — no panel, no border, no card
 * chrome. Eyebrow above the title, optional subtitle below, optional
 * trailing actions. The page wrapper handles horizontal padding.
 *
 * Use on home / list screens (Classes, Planning, Schedule, Home).
 */

defineProps<{
  title: string
  /** Small uppercase eyebrow above the title (e.g. today's date). */
  eyebrow?: string
  subtitle?: string
}>()
</script>

<template>
  <header class="home-screen-header">
    <div class="home-screen-header-text">
      <p v-if="eyebrow" class="home-screen-header-eyebrow">{{ eyebrow }}</p>
      <h1 class="home-screen-header-title">{{ title }}</h1>
      <p v-if="subtitle" class="home-screen-header-subtitle">{{ subtitle }}</p>
    </div>
    <div v-if="$slots.actions" class="home-screen-header-actions">
      <slot name="actions" />
    </div>
  </header>
</template>

<style scoped>
.home-screen-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-md);
  /* No background, no border — sits flush on the page surface and lets
     the AppLayout's page padding handle horizontal alignment. */
}

.home-screen-header-text {
  min-width: 0;
}

.home-screen-header-eyebrow {
  font-family: var(--font-sans);
  font-size: var(--text-micro);
  line-height: var(--text-micro-lh);
  letter-spacing: var(--tracking-wide);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  color: var(--ink-400);
  margin: 0 0 var(--space-6);
}

.home-screen-header-title {
  font-family: var(--font-sans);
  font-size: var(--text-title-xl);
  line-height: var(--text-title-xl-lh);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-tight);
  color: var(--ink-900);
  margin: 0;
}

.home-screen-header-subtitle {
  font-family: var(--font-sans);
  font-size: var(--text-body-sm);
  line-height: var(--text-body-sm-lh);
  color: var(--ink-600);
  margin: var(--space-6) 0 0;
}

.home-screen-header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-shrink: 0;
}
</style>
